import React from "react";
import { motion } from "framer-motion";
import { Brain, UserX, LineChart, Scaling } from "lucide-react";

const CHALLENGES = [
  {
    icon: Brain,
    title: "Ineffective learning",
    supportLine: "Passive classrooms lead to low retention and shallow skill development across your team.",
    tone: "from-blue-50 to-indigo-50 border-blue-100",
    accent: "from-blue-500 to-indigo-500",
  },
  {
    icon: UserX,
    title: "Lack of personalization",
    supportLine: "One-size-fits-all content fails to adapt to specific roles, leaving individuals disengaged.",
    tone: "from-violet-50 to-fuchsia-50 border-violet-100",
    accent: "from-violet-500 to-fuchsia-500",
  },
  {
    icon: LineChart,
    title: "Poor measurement",
    supportLine: "Completion rates offer blind spots without clear links to actual performance improvements.",
    tone: "from-teal-50 to-emerald-50 border-teal-100",
    accent: "from-teal-500 to-emerald-500",
  },
  {
    icon: Scaling,
    title: "Scalability issues",
    supportLine: "Instructor bottlenecks and timezone constraints make it difficult to grow programs consistently.",
    tone: "from-amber-50 to-orange-50 border-amber-100",
    accent: "from-amber-500 to-orange-500",
  },
];

export default function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 sm:py-28 bg-gradient-to-b from-white to-blue-50/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100"
          >
            The problem
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-5 text-[34px] sm:text-[46px] leading-[1.08] font-extrabold tracking-tight text-slate-900 text-balance"
            data-testid="problem-heading"
          >
            Core challenges in corporate training today.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-5 text-[16.5px] leading-relaxed text-slate-600"
          >
            Traditional training models were built for classrooms, not for
            today’s distributed, fast-moving, skill-hungry workforce.
          </motion.p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CHALLENGES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8% 0px" }}
                transition={{ duration: 0.65, delay: i * 0.08 }}
                className={`card-lift relative overflow-hidden bg-gradient-to-br ${c.tone} border rounded-[22px] p-6 flex flex-col`}
                data-testid={`problem-card-${i}`}
              >
                <div
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${c.accent} text-white shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]`}
                >
                  <Icon size={20} strokeWidth={2} />
                </div>
                <h3 className="mt-6 text-[22px] sm:text-[24px] font-extrabold tracking-tight text-slate-900 leading-tight">
                  {c.title}
                </h3>
                <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed font-medium flex-1">
                  {c.supportLine}
                </p>
                <div
                  className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full opacity-40 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(255,255,255,0.8), transparent 70%)",
                  }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
